'use client';

import { c as _c } from "react/compiler-runtime";
import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';
import { useContext, useEffect, useRef } from 'react';
import { CartContext, Cookie, decodeJwt, getOrigin, PageType, useCustomerAutoshipSubscriptions, useLoyaltyContext, usePageLayout, useUserSession } from '@libs/common-next';
import { AUTH_COOKIES, COOKIES, getOktaIdToken } from '@libs/core';
import { dataLayerPush, trackError } from '../gtm';
import { clearFreeShippingMeterStorage, getFreeShippingMeterAnalyticsValue, getStoredFreeShippingMeterValue, isFreeShippingMeterLockedForConfirmation, pageDataMapper, syncFreeShippingMeterStorage, unlockFreeShippingMeterForOrderConfirmation } from '../helpers';
import type { EventTracking } from '../types';
import { getOrderPlatformFromPath } from '../utils/getOrderPlatformFromPath';
const PAGE_REFERRER_KEY = 'ps_page_referrer';
const isOrderConfirmationPath = (pathname: string) => pathname.includes('/order-confirmation');
const isFreeShippingMeterPage = (pageType: string, pathname: string) => isOrderConfirmationPath(pathname) || pageType === PageType.CART || pageType === PageType.CHECKOUT;
export const usePageViewTracking = () => {
  const $ = _c(41);
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getOrigin();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const origin = t0;
  const {
    pageType,
    userData,
    user
  } = usePageLayout();
  const {
    memberPoints,
    isLoading
  } = useLoyaltyContext();
  const {
    isSoftAuthenticated
  } = useUserSession();
  const {
    autoshipData,
    isLoading: isAutoshipLoading
  } = useCustomerAutoshipSubscriptions();
  const cartContext = useContext(CartContext);
  const cart = cartContext?.cart ?? null;
  const isCartLoading = cartContext?.isLoading ?? false;
  const previousPathRef = useRef(null);
  const hasTrackedPageRef = useRef(false);
  let t1;
  if ($[1] !== autoshipData || $[2] !== user || $[3] !== userData) {
    t1 = eventData => {
      if (userData && user && autoshipData) {
        eventData.autoship_customer = autoshipData.data.hasAutoship;
        eventData.first_autoship_eligible = autoshipData.data.isFirstAutoshipEligible;
      }
    };
    $[1] = autoshipData;
    $[2] = user;
    $[3] = userData;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const enrichEventDataWithAutoship = t1;
  const getPageReferrer = _temp;
  let t2;
  if ($[5] !== cart || $[6] !== pageType) {
    t2 = currentPath => {
      if (!isFreeShippingMeterPage(pageType, currentPath)) {
        if (isFreeShippingMeterLockedForConfirmation()) {
          clearFreeShippingMeterStorage();
        }
        return null;
      }
      if (isOrderConfirmationPath(currentPath)) {
        const storedValue = getStoredFreeShippingMeterValue();
        unlockFreeShippingMeterForOrderConfirmation();
        return storedValue;
      }
      const value = getFreeShippingMeterAnalyticsValue(cart);
      syncFreeShippingMeterStorage(value);
      return value;
    };
    $[5] = cart;
    $[6] = pageType;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const resolveFreeShippingMeter = t2;
  let t3;
  if ($[8] !== enrichEventDataWithAutoship || $[9] !== isSoftAuthenticated || $[10] !== memberPoints?.availablePoints || $[11] !== memberPoints?.currentTierRank || $[12] !== pageType || $[13] !== pathname || $[14] !== resolveFreeShippingMeter || $[15] !== user?.loyaltyid || $[16] !== userData) {
    t3 = currentPath_0 => {
      const cartId = Cookies.get(COOKIES.CART_ID);
      const mappedPageData = pageDataMapper(pageType, currentPath_0);
      const custom_device_id = Cookie.get("x-petm-visitor-id");
      const oktaIdToken = getOktaIdToken();
      const decodedAosJwt = oktaIdToken ? decodeJwt(oktaIdToken)?.payload : null;
      const loginStatus = isSoftAuthenticated ? "soft" : Boolean(userData);
      const platform = getOrderPlatformFromPath(currentPath_0);
      const pageReferrer = getPageReferrer();
      const currentPageLocation = `${origin}${currentPath_0}`;
      const eventData_0 = {
        event: "page_view",
        page_location: currentPageLocation,
        page_title: document.title,
        page_referrer: pageReferrer,
        platform,
        is_aos_user: !!oktaIdToken,
        aos_email: oktaIdToken ? decodedAosJwt?.email : null,
        login_status: loginStatus,
        loyalty_id: user?.loyaltyid,
        treats_points: userData ? memberPoints?.availablePoints ?? null : null,
        email: userData?.profile?.emailAddress ?? null,
        treats_tier: userData && memberPoints?.currentTierRank ? `tier ${String(memberPoints?.currentTierRank)}` : null,
        cart_id: cartId,
        custom_device_id,
        autoship_customer: null,
        free_shipping_meter: resolveFreeShippingMeter(currentPath_0),
        ...mappedPageData
      };
      enrichEventDataWithAutoship(eventData_0);
      dataLayerPush(eventData_0);
      if (globalThis.window !== undefined) {
        sessionStorage.setItem(PAGE_REFERRER_KEY, currentPageLocation);
      }
      if (mappedPageData.page_type === "404 error") {
        trackError(`${pathname}`, "404");
      }
      if (pageType === PageType.ACP) {
        dataLayerPush({
          event: "event_accountmanagement",
          pageName: `ps:Account Management${currentPath_0.split("/").map(_temp2).join(" ")}`,
          platform
        });
      }
    };
    $[8] = enrichEventDataWithAutoship;
    $[9] = isSoftAuthenticated;
    $[10] = memberPoints?.availablePoints;
    $[11] = memberPoints?.currentTierRank;
    $[12] = pageType;
    $[13] = pathname;
    $[14] = resolveFreeShippingMeter;
    $[15] = user?.loyaltyid;
    $[16] = userData;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const firePageView = t3;
  let t4;
  let t5;
  if ($[18] !== cart || $[19] !== isCartLoading || $[20] !== pathname) {
    t4 = () => {
      if (isCartLoading || isOrderConfirmationPath(pathname ?? "")) {
        return;
      }
      syncFreeShippingMeterStorage(getFreeShippingMeterAnalyticsValue(cart));
    };
    t5 = [cart, isCartLoading, pathname];
    $[18] = cart;
    $[19] = isCartLoading;
    $[20] = pathname;
    $[21] = t4;
    $[22] = t5;
  } else {
    t4 = $[21];
    t5 = $[22];
  }
  useEffect(t4, t5);
  let t6;
  if ($[23] !== firePageView || $[24] !== isAutoshipLoading || $[25] !== isCartLoading || $[26] !== isLoading || $[27] !== memberPoints || $[28] !== pageType || $[29] !== pathname || $[30] !== userData) {
    t6 = () => {
      const hasAccessToken = !!Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);
      if (!pathname) {
        return;
      }
      const pathnameChanged = previousPathRef.current !== pathname;
      if (pathnameChanged) {
        if (previousPathRef.current !== null && globalThis.window !== undefined) {
          sessionStorage.setItem(PAGE_REFERRER_KEY, `${origin}${previousPathRef.current}`);
        }
        previousPathRef.current = pathname;
        hasTrackedPageRef.current = false;
      }
      const shouldWaitForData = hasAccessToken && (!userData || !memberPoints && isLoading || isAutoshipLoading);
      if (shouldWaitForData) {
        return;
      }
      const shouldWaitForCart = isFreeShippingMeterPage(pageType, pathname) && !isOrderConfirmationPath(pathname) && isCartLoading;
      if (shouldWaitForCart) {
        return;
      }
      if (!hasTrackedPageRef.current) {
        firePageView(pathname);
        hasTrackedPageRef.current = true;
      }
    };
    $[23] = firePageView;
    $[24] = isAutoshipLoading;
    $[25] = isCartLoading;
    $[26] = isLoading;
    $[27] = memberPoints;
    $[28] = pageType;
    $[29] = pathname;
    $[30] = userData;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== cart || $[33] !== isAutoshipLoading || $[34] !== isCartLoading || $[35] !== isLoading || $[36] !== memberPoints || $[37] !== pageType || $[38] !== pathname || $[39] !== userData) {
    t7 = [pathname, userData, memberPoints, isLoading, isAutoshipLoading, isCartLoading, cart, pageType];
    $[32] = cart;
    $[33] = isAutoshipLoading;
    $[34] = isCartLoading;
    $[35] = isLoading;
    $[36] = memberPoints;
    $[37] = pageType;
    $[38] = pathname;
    $[39] = userData;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  useEffect(t6, t7);
};
function _temp() {
  if (globalThis.window === undefined) {
    return "";
  }
  return sessionStorage.getItem(PAGE_REFERRER_KEY) ?? "";
}
function _temp2(str) {
  return str.split("-").join(" ");
}